// ============================================================================
//  CRM Module — Phase 5 (corrigé audit)
//  Pages : Pipeline Kanban, Contacts, Entreprises, Activités, Leaderboard,
//          Commissions, Dashboard
//  Corrections : deleteCompany (appel API dédié), deleteOpportunity, deleteContact,
//                contestCommission, séquences sur contact, import types complets
// ============================================================================

import { ListPageSkeleton } from '@/components/ui/Skeleton'
import { useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  TrendingUp, Users, Building2, Activity, Target, Trophy, DollarSign,
  Plus, Search, X, Flame, Thermometer, Snowflake, Phone, Mail, Video,
  MessageCircle, Calendar, Star, Award, Crown, Medal, ChevronRight,
  Check, MoreVertical, Edit2, Trash2, BarChart3, Zap, AlertCircle,
  Archive, BookOpen, ArrowRight, ChevronDown, ChevronUp, Clock, RefreshCw,
} from 'lucide-react'
import {
  crmApi,
  type CrmOpportunity, type PipelineColumn, type CrmContact,
  type CrmCompany, type LeaderboardEntry, type Commission,
} from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore }          from '@/stores/auth.store'
import { CompanyCombobox, ContactCombobox } from '@/modules/crm/components/CrmComboboxes'
import { ActivityCorrectedBadge, ActivityCorrectionList, CorrectActivityButton } from '@/modules/crm/components/ActivityCorrections'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toNum = (v: any): number => {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return v
  if (typeof v === 'string') return parseFloat(v) || 0
  if (typeof v === 'object' && Array.isArray(v.d) && typeof v.e === 'number' && v.d.length > 0) {
    const sign = v.s < 0 ? -1 : 1
    const d0 = v.d[0]
    return sign * d0 * Math.pow(10, v.e - String(d0).length + 1)
  }
  return Number(v) || 0
}

const fmtMoney = (n?: any, currency = 'XOF') => {
  const v = toNum(n)
  if (!v && v !== 0) return '—'
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v)
}
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-SN') : '—'

function fmtRelative(d: string) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000
  if (diff < 60)    return "À l'instant"
  if (diff < 3600)  return `Il y a ${Math.floor(diff / 60)}min`
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`
  return new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const SCORE_CONFIG: Record<string, { label: string; icon: typeof Flame; color: string; bg: string }> = {
  HOT:  { label: 'Chaud', icon: Flame,       color: 'text-[#DC2626]', bg: 'bg-[#F87171]/10' },
  WARM: { label: 'Tiède', icon: Thermometer, color: 'text-[#EA580C]', bg: 'bg-[#F97316]/10' },
  COLD: { label: 'Froid', icon: Snowflake,   color: 'text-[var(--blue-d)]', bg: 'bg-[#38BDF8]/10' },
}

const ACTIVITY_ICONS: Record<string, typeof Phone> = {
  CALL: Phone, EMAIL: Mail, MEETING: Calendar, VIDEO_CALL: Video,
  WHATSAPP: MessageCircle, DEMO: Star, NOTE: Activity, OTHER: Activity,
}

const STATUS_COLORS: Record<string, string> = {
  PENDING:          'bg-[#D6D2EC]/20 text-[var(--tx2)]',
  MANAGER_APPROVED: 'bg-[#BAE6FD] text-blue-700',
  HR_APPROVED:      'bg-indigo-100 text-indigo-700',
  FINANCE_APPROVED: 'bg-purple-100 text-purple-700',
  INTEGRATED:       'bg-emerald-100 text-emerald-700',
  PAID:             'bg-green-100 text-green-700',
  REJECTED:         'bg-red-100 text-red-700',
  CONTESTED:        'bg-amber-100 text-amber-700',
}

const RANK_ICONS = [Crown, Trophy, Medal]

const LEAD_SOURCES = [
  { value: 'WEBSITE',        label: 'Site web' },
  { value: 'REFERRAL',       label: 'Référence' },
  { value: 'LINKEDIN',       label: 'LinkedIn' },
  { value: 'EVENT',          label: 'Événement' },
  { value: 'COLD_CALL',      label: 'Appel à froid' },
  { value: 'EMAIL_CAMPAIGN', label: 'Campagne email' },
  { value: 'PARTNER',        label: 'Partenaire' },
  { value: 'OTHER',          label: 'Autre' },
]

const COMPANY_SIZES = [
  { value: 'MICRO',      label: 'Micro (1-9)' },
  { value: 'SMALL',      label: 'Petite (10-49)' },
  { value: 'MEDIUM',     label: 'Moyenne (50-249)' },
  { value: 'LARGE',      label: 'Grande (250-999)' },
  { value: 'ENTERPRISE', label: 'Entreprise (1000+)' },
]

const INDUSTRIES = [
  'Agriculture','Agroalimentaire','BTP & Immobilier','Commerce & Distribution',
  'Communication & Médias','Conseil & Services','Éducation & Formation',
  'Finance & Assurance','Industrie & Manufacturing','Logistique & Transport',
  'Numérique & Tech','Pétrole & Mines','Santé & Pharma','Télécommunications',
  'Tourisme & Hôtellerie','Autre',
]

const ACTIVITY_CONFIG: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  CALL:       { label: 'Appel',    icon: '📞', color: 'var(--blue-d)', bg: '#EFF6FF' },
  EMAIL:      { label: 'Email',    icon: '✉️',  color: '#7C3AED', bg: '#F5F3FF' },
  MEETING:    { label: 'Réunion',  icon: '🤝', color: '#059669', bg: '#ECFDF5' },
  VIDEO_CALL: { label: 'Visio',    icon: '🎥', color: '#DB2777', bg: '#FDF2F8' },
  WHATSAPP:   { label: 'WhatsApp', icon: '💬', color: '#16A34A', bg: '#F0FDF4' },
  DEMO:       { label: 'Démo',     icon: '🖥️',  color: '#D97706', bg: '#FFFBEB' },
  NOTE:       { label: 'Note',     icon: '📝', color: 'var(--brand-amber)', bg: 'var(--blue-bg)' },
  OTHER:      { label: 'Autre',    icon: '📌', color: 'var(--tx3)', bg: '#F4F2FC' },
}

const INTEREST_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  LOW:       { label: 'Faible',     color: 'var(--tx3)', bg: '#F4F2FC' },
  MEDIUM:    { label: 'Moyen',      color: '#D97706', bg: '#FFFBEB' },
  HIGH:      { label: 'Élevé',      color: '#059669', bg: '#ECFDF5' },
  VERY_HIGH: { label: 'Très élevé', color: '#DC2626', bg: '#FEF2F2' },
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────

const Spinner = () => (
  <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
)

function Avatar({ user, size = 'sm' }: { user?: any; size?: 'xs' | 'sm' | 'md' }) {
  if (!user) return <div className="w-8 h-8 rounded-full bg-[var(--surface-2)]" />
  const s = { xs: 'w-6 h-6 text-[10px]', sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm' }[size]
  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`
  return user.avatarUrl ? (
    <img src={user.avatarUrl} alt={initials} className={`${s} rounded-full object-cover shrink-0`} />
  ) : (
    <div className={`${s} rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] font-bold shrink-0`}>
      {initials}
    </div>
  )
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className={`bg-white border border-[var(--bd)] rounded-2xl w-full shadow-2xl max-h-[90vh] flex flex-col ${wide ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">{label}</label>
      {children}
    </div>
  )
}

const inputCls = 'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]'

// ============================================================================
//  CRM DASHBOARD
// ============================================================================

export default function CrmDashboard() {
  const navigate = useNavigate()
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['crm-dashboard'],
    queryFn:  () => crmApi.getDashboard(),
  })

  if (isLoading) return <ListPageSkeleton />

  const kpis     = dashboard?.kpis ?? {}
  const pipeline = dashboard?.pipelineByStage ?? []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Pipeline Commercial</h1>
          <p className="text-[var(--tx3)] text-sm mt-0.5">Tableau de bord CRM</p>
        </div>
        <button onClick={() => navigate('/crm/pipeline')}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Nouvelle opportunité
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
        {[
          { label: 'Opportunités actives', value: kpis.totalOpportunities ?? 0, icon: Activity, color: 'text-[var(--blue-d)]', bg: 'bg-[#38BDF8]/10' },
          { label: 'Deals gagnés (mois)',  value: kpis.wonThisMonth ?? 0,       icon: Check,    color: 'text-[#059669]', bg: 'bg-[#10B981]/10' },
          { label: 'CA closé (mois)',  value: fmtMoney(kpis.wonRevenue), icon: DollarSign, color: 'text-[#059669]', bg: 'bg-[#10B981]/10', isText: true },
          { label: 'Forecast pondéré', value: fmtMoney(kpis.forecast),   icon: TrendingUp,  color: 'text-[#7C3AED]', bg: 'bg-purple-400/10', isText: true },
        ].map(k => (
          <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--tx3)]">{k.label}</span>
              <div className={`p-1.5 ${k.bg} rounded-lg`}><k.icon className={`w-3.5 h-3.5 ${k.color}`} /></div>
            </div>
            <p className={`font-bold text-[var(--brand-dark)] ${k.isText ? 'text-lg' : 'text-2xl'}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-4">Pipeline par étape</h2>
        <div className="space-y-2">
          {pipeline.map((col: any) => {
            const maxVal = Math.max(...pipeline.map((c: any) => c.value), 1)
            const pct = Math.round((col.value / maxVal) * 100)
            return (
              <div key={col.stage.id} className="flex items-center gap-3">
                <div className="w-36 shrink-0"><span className="text-xs text-[var(--tx3)] truncate">{col.stage.name}</span></div>
                <div className="flex-1 h-2 bg-[var(--surface-2)] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: col.stage.color }} />
                </div>
                <div className="w-20 text-right"><span className="text-xs text-[var(--tx2)]">{col.count} opp.</span></div>
                <div className="w-28 text-right"><span className="text-xs text-[var(--tx3)]">{fmtMoney(col.value)}</span></div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Pipeline Kanban', icon: BarChart3, to: '/crm/pipeline' },
          { label: 'Contacts',        icon: Users,     to: '/crm/contacts' },
          { label: 'Leaderboard',     icon: Trophy,    to: '/crm/leaderboard' },
          { label: 'Commissions',     icon: DollarSign, to: '/crm/commissions' },
          { label: 'Objectifs',       icon: Target,    to: '/crm/targets' },
          { label: 'Séquences drip',  icon: Zap,       to: '/crm/sequences' },
        ].map(a => (
          <button key={a.to} onClick={() => navigate(a.to)}
            className="flex items-center gap-3 p-4 bg-[var(--bg)] border border-[var(--bd)] rounded-xl hover:bg-[var(--surface-2)]/40 hover:border-[var(--blue)]/30 transition-all text-left">
            <a.icon className="w-5 h-5 text-[var(--blue-d)] shrink-0" />
            <span className="text-sm text-[var(--brand-dark)] font-medium">{a.label}</span>
            <ChevronRight className="w-4 h-4 text-[var(--tx3)] ml-auto" />
          </button>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
//  PIPELINE KANBAN
// ============================================================================

export function PipelinePage() {
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const [dragOppId, setDragOppId]     = useState<string | null>(null)
  const [dragStageId, setDragStageId] = useState<string | null>(null)
  const [showCreate, setShowCreate]   = useState(false)
  const navigate                       = useNavigate()
  const [showJournal, setShowJournal] = useState<string | null>(null)
  const [search, setSearch]           = useState('')

  const { data: columns = [], isLoading } = useQuery({
    queryKey: ['crm-pipeline'],
    queryFn:  () => crmApi.getPipeline(),
  })

  const moveMutation = useMutation({
    mutationFn: ({ id, stageId, wonLostReason }: any) => crmApi.moveStage(id, { stageId, wonLostReason }),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Opportunité déplacée' })
    },
    onError: (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur lors du déplacement' }),
  })

  const deleteOppMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteOpportunity(id),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Opportunité archivée' })
    },
  })

  const handleDrop = (stageId: string) => {
    if (!dragOppId || stageId === dragStageId) return
    moveMutation.mutate({ id: dragOppId, stageId })
    setDragOppId(null); setDragStageId(null)
  }

  if (isLoading) return <ListPageSkeleton />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Pipeline de vente</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
              className="pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          </div>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium">
            <Plus className="w-4 h-4" /> Opportunité
          </button>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 min-h-[70vh]">
        {(columns as PipelineColumn[]).map(col => (
          <KanbanColumn key={col.stage.id} col={col} search={search}
            onDragStart={oppId => { setDragOppId(oppId); setDragStageId(col.stage.id) }}
            onDrop={() => handleDrop(col.stage.id)}
            onClickOpp={opp => navigate(`/crm/opportunities/${opp.id}`)}
            onAddJournal={oppId => setShowJournal(oppId)}
            onDeleteOpp={id => window.confirm('Archiver cette opportunité ?') && deleteOppMutation.mutate(id)}
          />
        ))}
      </div>

      {showCreate && <CreateOpportunityModal onClose={() => setShowCreate(false)} onCreated={() => { queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] }); setShowCreate(false) }} />}
      {showJournal && <JournalModal opportunityId={showJournal} onClose={() => setShowJournal(null)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] }); setShowJournal(null) }} />}
    </div>
  )
}

function KanbanColumn({ col, search, onDragStart, onDrop, onClickOpp, onAddJournal, onDeleteOpp }: {
  col: PipelineColumn; search: string
  onDragStart: (id: string) => void; onDrop: () => void
  onClickOpp: (o: CrmOpportunity) => void; onAddJournal: (id: string) => void
  onDeleteOpp: (id: string) => void
}) {
  const [isDragOver, setIsDragOver] = useState(false)
  const filtered = useMemo(() =>
    search ? col.opportunities.filter(o => o.title.toLowerCase().includes(search.toLowerCase())) : col.opportunities,
    [col.opportunities, search])

  return (
    <div className={`w-72 shrink-0 flex flex-col rounded-xl border transition-colors ${isDragOver ? 'border-blue-500/50 bg-[var(--blue)]/5' : 'border-[var(--bd)] bg-[var(--bg)]'}`}
      onDragOver={e => { e.preventDefault(); setIsDragOver(true) }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={() => { setIsDragOver(false); onDrop() }}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--bd)]">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.stage.color ?? 'var(--tx3)' }} />
          <span className="text-xs font-semibold text-[var(--brand-dark)] truncate">{col.stage.name}</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded-full">{filtered.length}</span>
        </div>
        <span className="text-[10px] text-[var(--tx3)]">{fmtMoney(col.totalValue)}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {filtered.map(opp => (
          <OppCard key={opp.id} opp={opp}
            onDragStart={() => onDragStart(opp.id)}
            onClick={() => onClickOpp(opp)}
            onAddJournal={() => onAddJournal(opp.id)}
            onDelete={() => onDeleteOpp(opp.id)}
          />
        ))}
        {filtered.length === 0 && <div className="text-center py-8 text-[var(--tx3)] text-xs">Aucune opportunité</div>}
      </div>
    </div>
  )
}

function OppCard({ opp, onDragStart, onClick, onAddJournal, onDelete }: {
  opp: CrmOpportunity; onDragStart: () => void; onClick: () => void; onAddJournal: () => void; onDelete: () => void
}) {
  const score    = SCORE_CONFIG[opp.scoreLevel]
  const ScoreIcon = score.icon
  const user     = opp.assignedTo?.user

  return (
    <div draggable onDragStart={onDragStart} onClick={onClick}
      className="bg-white border border-[var(--bd)] rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-blue-500/30 hover:bg-[var(--surface-2)]/80 transition-all group">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold text-[var(--brand-dark)] leading-tight line-clamp-2">{opp.title}</h3>
        <div className={`flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-md ${score.bg}`}>
          <ScoreIcon className={`w-3 h-3 ${score.color}`} />
        </div>
      </div>
      {opp.company && (
        <p className="text-[10px] text-[var(--tx3)] mb-2 flex items-center gap-1">
          <Building2 className="w-3 h-3" /> {opp.company.name}
        </p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-[#059669]">{fmtMoney(opp.amount, opp.currency)}</span>
        <div className="flex items-center gap-1.5">
          {user && <Avatar user={user} size="xs" />}
          <button onClick={e => { e.stopPropagation(); onAddJournal() }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[var(--bd)] rounded" title="Ajouter activité">
            <Activity className="w-3 h-3 text-[var(--blue-d)]" />
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete() }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded" title="Archiver">
            <Archive className="w-3 h-3 text-[var(--tx3)]" />
          </button>
        </div>
      </div>
      {opp.expectedCloseDate && (
        <p className="text-[10px] text-[var(--tx3)] mt-1.5">Clôture : {fmtDate(opp.expectedCloseDate)}</p>
      )}
    </div>
  )
}

// ============================================================================
//  CONTACTS
// ============================================================================

export function ContactsPage() {
  const [search, setSearch]         = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editContact, setEditContact] = useState<CrmContact | null>(null)
  const [seqContact, setSeqContact]   = useState<CrmContact | null>(null)
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)

  const { data, isLoading } = useQuery({
    queryKey: ['crm-contacts', search],
    queryFn:  () => crmApi.listContacts({ search, limit: 50 }),
  })
  const contacts: CrmContact[] = data?.items ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteContact(id),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Contact désactivé' })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Contacts</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
              className="pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          </div>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium">
            <Plus className="w-4 h-4" /> Contact
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Nom','Entreprise','Email','Téléphone','Source','Opps','Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {contacts.map(c => (
                <tr key={c.id} className="hover:bg-[var(--surface-2)]/50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] text-xs font-bold">
                        {c.firstName[0]}{c.lastName[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--brand-dark)]">{c.firstName} {c.lastName}</p>
                        {c.jobTitle && <p className="text-[10px] text-[var(--tx3)]">{c.jobTitle}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)]">{c.company?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-[var(--tx3)]">{c.email ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-[var(--tx3)]">{c.phone ?? (c as any).mobile ?? '—'}</td>
                  <td className="px-4 py-3">
                    {c.source && <span className="text-[10px] px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx2)] rounded-full">{c.source}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)] text-center">{c._count?.opportunities ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setEditContact(c)} className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--blue)] hover:bg-[var(--surface-2)]" title="Modifier"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setSeqContact(c)} className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[#7C3AED] hover:bg-[var(--surface-2)]" title="Séquence email"><Zap className="w-3.5 h-3.5" /></button>
                      <button onClick={() => window.confirm(`Désactiver ${c.firstName} ${c.lastName} ?`) && deleteMutation.mutate(c.id)}
                        className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-red-500 hover:bg-red-50" title="Désactiver"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-[var(--tx3)] text-sm">Aucun contact</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && <CreateContactModal onClose={() => setShowCreate(false)} onCreated={() => { queryClient.invalidateQueries({ queryKey: ['crm-contacts'] }); setShowCreate(false) }} />}
      {editContact && <EditContactModal contact={editContact} onClose={() => setEditContact(null)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-contacts'] }); setEditContact(null) }} />}
      {seqContact && <ContactSequenceModal contact={seqContact} onClose={() => setSeqContact(null)} />}
    </div>
  )
}

// ============================================================================
//  LEADERBOARD
// ============================================================================

export function LeaderboardPage() {
  const [period, setPeriod] = useState<'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR'>('MONTH')
  const { data, isLoading } = useQuery({
    queryKey: ['crm-leaderboard', period],
    queryFn:  () => crmApi.getLeaderboard(period),
    refetchInterval: 30000,
  })
  const leaderboard: LeaderboardEntry[] = data?.leaderboard ?? []
  const PERIOD_LABELS = { WEEK: 'Semaine', MONTH: 'Mois', QUARTER: 'Trimestre', YEAR: 'Année' }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">🏆 Leaderboard</h1>
          <p className="text-[var(--tx3)] text-xs mt-0.5">Classement en temps réel · Rafraîchi toutes les 30s</p>
        </div>
        <div className="flex gap-1">
          {(Object.keys(PERIOD_LABELS) as Array<keyof typeof PERIOD_LABELS>).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p ? 'bg-[var(--blue)] text-white' : 'bg-white text-[var(--tx3)] hover:text-[var(--tx)]'}`}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <div className="flex justify-center py-12"><Spinner /></div>
      : leaderboard.length === 0 ? <div className="text-center py-12 text-[var(--tx3)]">Aucune activité sur cette période</div>
      : (
        <div className="space-y-3">
          {leaderboard.map(entry => {
            const RankIcon = RANK_ICONS[entry.rank - 1]
            const user     = entry.employee?.user
            return (
              <div key={entry.employee?.id}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${entry.rank === 1 ? 'bg-[#FFFBEB] border-amber-200' : entry.rank === 2 ? 'bg-[var(--tx3)]/5 border-[var(--tx3)]/20' : entry.rank === 3 ? 'bg-[#FFF7ED] border-amber-200' : 'bg-[var(--bg)] border-[var(--bd)]'}`}>
                <div className="w-10 flex justify-center shrink-0">
                  {RankIcon ? <RankIcon className={`w-6 h-6 ${entry.rank === 1 ? 'text-[#D97706]' : entry.rank === 2 ? 'text-[var(--tx2)]' : 'text-amber-600'}`} />
                  : <span className="text-[var(--tx3)] font-bold text-sm">#{entry.rank}</span>}
                </div>
                <Avatar user={user} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--brand-dark)]">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-[var(--tx3)]">{entry.activities} activités</p>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-right"><p className="text-xs text-[var(--tx3)]">Deals gagnés</p><p className="text-sm font-bold text-[#059669]">{entry.dealsWon}</p></div>
                  <div className="text-right"><p className="text-xs text-[var(--tx3)]">CA closé</p><p className="text-sm font-bold text-[var(--brand-dark)]">{fmtMoney(entry.totalRevenue)}</p></div>
                  <div className="text-right"><p className="text-xs text-[var(--tx3)]">Commission</p><p className="text-sm font-bold text-purple-600">{fmtMoney(entry.commission)}</p></div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  COMMISSIONS
// ============================================================================

export function CommissionsPage() {
  const [statusFilter, setStatusFilter] = useState('')
  const [contestModal, setContestModal] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const { hasPermission } = useAuthStore()
  const canValidate = hasPermission('crm:configure')

  const { data, isLoading } = useQuery({
    queryKey: ['crm-commissions', statusFilter],
    queryFn:  () => crmApi.listCommissions({ status: statusFilter || undefined }),
  })
  const commissions: Commission[] = data?.items ?? []

  const validateMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) => crmApi.validateCommission(id, { action }),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Commission mise à jour' }) },
  })

  const contestMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => crmApi.contestCommission(id, reason),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); setContestModal(null); addNotif({ id: Date.now().toString(), type: 'success', title: 'Commission contestée' }) },
    onError:    (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Commissions</h1>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] focus:outline-none">
          <option value="">Tous les statuts</option>
          {['PENDING','MANAGER_APPROVED','HR_APPROVED','FINANCE_APPROVED','INTEGRATED','PAID','CONTESTED','REJECTED'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Commercial','Opportunité','Montant deal','Commission','Statut','Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--tx3)] uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {commissions.map(c => (
                <tr key={c.id} className="hover:bg-[var(--surface-2)]/50 transition-colors">
                  <td className="px-4 py-3"><p className="text-sm text-[var(--brand-dark)]">{c.employee?.user?.firstName} {c.employee?.user?.lastName}</p></td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)] max-w-xs truncate">{c.opportunity?.title ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)]">{fmtMoney(c.opportunity?.amount)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#059669]">{fmtMoney(c.amount, c.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] ?? 'bg-[var(--surface-2)] text-[var(--tx3)]'}`}>
                      {c.status.replace(/_/g, ' ')}
                    </span>
                    {c.contestReason && <p className="text-[10px] text-amber-600 mt-0.5 max-w-xs truncate" title={c.contestReason}>⚠ {c.contestReason}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {canValidate && c.status === 'PENDING' && (
                        <>
                          <button onClick={() => validateMutation.mutate({ id: c.id, action: 'MANAGER_APPROVED' })}
                            className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded hover:bg-emerald-100">Approuver</button>
                          <button onClick={() => validateMutation.mutate({ id: c.id, action: 'REJECTED' })}
                            className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded hover:bg-red-100">Rejeter</button>
                        </>
                      )}
                      {canValidate && c.status === 'MANAGER_APPROVED' && (
                        <button onClick={() => validateMutation.mutate({ id: c.id, action: 'HR_APPROVED' })}
                          className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded hover:bg-indigo-100">Valider RH</button>
                      )}
                      {canValidate && c.status === 'HR_APPROVED' && (
                        <button onClick={() => validateMutation.mutate({ id: c.id, action: 'FINANCE_APPROVED' })}
                          className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded hover:bg-purple-100">Valider Finance</button>
                      )}
                      {/* Contester — disponible pour les statuts en cours de validation */}
                      {['PENDING','MANAGER_APPROVED','HR_APPROVED'].includes(c.status) && (
                        <button onClick={() => setContestModal(c.id)}
                          className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded hover:bg-amber-100">Contester</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {commissions.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--tx3)] text-sm">Aucune commission</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal contestation */}
      {contestModal && (
        <ContestCommissionModal
          onClose={() => setContestModal(null)}
          onSubmit={reason => contestMutation.mutate({ id: contestModal, reason })}
          isLoading={contestMutation.isPending}
        />
      )}
    </div>
  )
}

function ContestCommissionModal({ onClose, onSubmit, isLoading }: { onClose: () => void; onSubmit: (reason: string) => void; isLoading: boolean }) {
  const [reason, setReason] = useState('')
  return (
    <Modal title="Contester la commission" onClose={onClose}>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-700">Expliquez pourquoi vous contestez cette commission. Minimum 10 caractères.</p>
      </div>
      <Field label="Motif de contestation *">
        <textarea className={inputCls + ' resize-none'} rows={4} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Ex: Le montant calculé ne correspond pas au deal validé le 15/03…" />
      </Field>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => onSubmit(reason)} disabled={isLoading || reason.trim().length < 10}
          className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isLoading ? <Spinner /> : <AlertCircle className="w-4 h-4" />} Contester
        </button>
      </div>
    </Modal>
  )
}

// ============================================================================
//  COMPANIES PAGE
// ============================================================================

export function CompaniesPage() {
  const queryClient     = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const [search, setSearch]         = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editCompany, setEditCompany] = useState<any | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['crm-companies', search],
    queryFn:  () => crmApi.listCompanies({ search: search || undefined, limit: 50 }),
  })
  const companies: any[] = data?.items ?? []

  // FIX: utilise le endpoint DELETE dédié au lieu de PATCH isActive
  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteCompany(id),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['crm-companies'] })
      addNotification({ type: 'success', title: 'Entreprise archivée', message: '' })
    },
  })

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>Entreprises</h1>
          <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 2 }}>{companies.length} entreprise{companies.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          <Plus className="w-4 h-4" /> Nouvelle entreprise
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--tx3)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une entreprise…"
          className="w-full pl-9 pr-4 py-2 rounded-xl text-sm focus:outline-none"
          style={{ background: '#FFFFFF', border: '1.5px solid var(--bd)', color: 'var(--brand-dark)' }} />
      </div>

      {isLoading ? <div className="flex justify-center py-12"><Spinner /></div>
      : companies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#F4F2FC' }}>
            <Building2 className="w-7 h-7" style={{ color: 'var(--brand-amber)' }} />
          </div>
          <div className="text-center">
            <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-dark)' }}>{search ? 'Aucune entreprise trouvée' : 'Aucune entreprise'}</p>
            <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 4 }}>{search ? 'Essayez un autre terme' : 'Créez votre première entreprise'}</p>
          </div>
          {!search && <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}><Plus className="w-4 h-4" /> Créer</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {companies.map((co: any) => (
            <div key={co.id} className="group flex items-center gap-4 p-4 rounded-xl transition-all cursor-pointer"
              style={{ background: '#FFFFFF', border: '1.5px solid var(--bd)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--brand-orange)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--bd)'}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm"
                style={{ background: 'linear-gradient(135deg, var(--brand-orange)22, var(--brand-amber)22)', color: 'var(--brand-amber)' }}>
                {co.name?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-dark)' }} className="truncate">{co.name}</p>
                  {co.size && <span style={{ fontSize: 10, color: 'var(--tx3)', background: '#F4F2FC', padding: '1px 7px', borderRadius: 10 }}>{COMPANY_SIZES.find(s => s.value === co.size)?.label ?? co.size}</span>}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {co.industry && <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{co.industry}</span>}
                  {co.city && <span style={{ fontSize: 12, color: 'var(--tx3)' }}>📍 {co.city}</span>}
                  {co.phone && <span style={{ fontSize: 12, color: 'var(--tx3)' }}>📞 {co.phone}</span>}
                </div>
              </div>
              <div className="hidden md:flex items-center gap-6 shrink-0">
                <div className="text-center"><p style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-dark)' }}>{co._count?.contacts ?? 0}</p><p style={{ fontSize: 11, color: 'var(--tx3)' }}>contacts</p></div>
                <div className="text-center"><p style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-dark)' }}>{co._count?.opportunities ?? 0}</p><p style={{ fontSize: 11, color: 'var(--tx3)' }}>opportunités</p></div>
              </div>
              {co.ninea && <span style={{ fontSize: 11, color: 'var(--brand-amber)', background: 'var(--blue-bg)', padding: '3px 10px', borderRadius: 8, flexShrink: 0 }}>NINEA: {co.ninea}</span>}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={e => { e.stopPropagation(); setEditCompany(co) }} className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--blue)]" title="Modifier"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={e => { e.stopPropagation(); window.confirm(`Archiver ${co.name} ?`) && deleteMutation.mutate(co.id) }} className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-red-500" title="Archiver"><Archive className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CompanyFormModal onClose={() => setShowCreate(false)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-companies'] }); addNotification({ type: 'success', title: 'Entreprise créée', message: '' }); setShowCreate(false) }} />}
      {editCompany && <CompanyFormModal company={editCompany} onClose={() => setEditCompany(null)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-companies'] }); addNotification({ type: 'success', title: 'Entreprise mise à jour', message: '' }); setEditCompany(null) }} />}
    </div>
  )
}

// ============================================================================
//  ACTIVITIES PAGE
// ============================================================================

export function ActivitiesPage() {
  const queryClient     = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const [typeFilter, setTypeFilter] = useState('')
  const [search, setSearch]         = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [expanded, setExpanded]     = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['crm-activities', typeFilter, search],
    queryFn:  () => crmApi.listActivities({ type: typeFilter || undefined, search: search || undefined, limit: 50 }),
  })
  const activities: any[] = data?.items ?? []

  const grouped = activities.reduce((acc: Record<string, any[]>, a) => {
    const day = new Date(a.performedAt).toLocaleDateString('fr-SN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    if (!acc[day]) acc[day] = []
    acc[day].push(a)
    return acc
  }, {})

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>Journal commercial</h1>
          <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 2 }}>{activities.length} activité{activities.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          <Plus className="w-4 h-4" /> Saisir une activité
        </button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--tx3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9 pr-4 py-2 rounded-xl text-sm focus:outline-none w-52" style={{ background: '#FFFFFF', border: '1.5px solid var(--bd)', color: 'var(--brand-dark)' }} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[{ value: '', label: 'Tous' }, ...Object.entries(ACTIVITY_CONFIG).map(([v, c]) => ({ value: v, label: c.icon + ' ' + c.label }))].map(opt => (
            <button key={opt.value} onClick={() => setTypeFilter(opt.value)} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{ background: typeFilter === opt.value ? 'var(--sb-bg)' : '#F4F2FC', color: typeFilter === opt.value ? 'var(--brand-orange)' : 'var(--brand-amber)' }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <div className="flex justify-center py-12"><Spinner /></div>
      : activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#F4F2FC' }}><Activity className="w-7 h-7" style={{ color: 'var(--brand-amber)' }} /></div>
          <div className="text-center">
            <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-dark)' }}>Aucune activité</p>
            <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 4 }}>Commencez à saisir vos interactions commerciales</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}><Plus className="w-4 h-4" /> Saisir</button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([day, acts]) => (
            <div key={day}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1" style={{ background: 'var(--bd)' }} />
                <span className="px-3 py-1 rounded-full text-xs font-semibold capitalize" style={{ background: '#F4F2FC', color: 'var(--brand-amber)' }}>{day}</span>
                <div className="h-px flex-1" style={{ background: 'var(--bd)' }} />
              </div>
              <div className="space-y-3">
                {acts.map((a: any) => {
                  const cfg      = ACTIVITY_CONFIG[a.type] ?? ACTIVITY_CONFIG.OTHER
                  const interest = INTEREST_CONFIG[a.interestLevel] ?? INTEREST_CONFIG.MEDIUM
                  const isOpen   = expanded === a.id
                  return (
                    <div key={a.id} className="rounded-xl transition-all cursor-pointer"
                      style={{ background: '#FFFFFF', border: `1.5px solid ${isOpen ? 'var(--brand-orange)' : 'var(--bd)'}` }}
                      onClick={() => setExpanded(isOpen ? null : a.id)}>
                      <div className="flex items-start gap-4 p-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0" style={{ background: cfg.bg }}>{cfg.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-lg" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                            <span className="text-xs px-2 py-0.5 rounded-lg font-medium" style={{ background: interest.bg, color: interest.color }}>{interest.label}</span>
                            {a.durationMinutes && <span className="text-xs" style={{ color: 'var(--tx3)' }}>⏱ {a.durationMinutes}min</span>}
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-dark)' }} className="truncate">{a.subject}</p>
                            <ActivityCorrectedBadge activity={a} />
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {a.contact    && <span style={{ fontSize: 12, color: 'var(--brand-amber)' }}>👤 {a.contact.firstName} {a.contact.lastName}</span>}
                            {a.opportunity && <span style={{ fontSize: 12, color: 'var(--blue-d)' }}>💼 {a.opportunity.title}</span>}
                            {/* Afficher le performedBy (User direct) */}
                            {a.performedBy && <span style={{ fontSize: 12, color: 'var(--tx3)' }}>par {a.performedBy.firstName} {a.performedBy.lastName}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p style={{ fontSize: 12, color: 'var(--tx3)' }}>{new Date(a.performedAt).toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit' })}</p>
                          <p style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>{fmtRelative(a.createdAt)}</p>
                          <div className="flex justify-end -mr-2 mt-1">
                            <CorrectActivityButton activity={a} invalidateKeys={[['crm-activities'], ['crm-opp-detail'], ['crm-opp']]} />
                          </div>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="px-4 pb-4 pt-0" style={{ borderTop: '1px solid var(--bd)' }}>
                          <div className="pt-3 space-y-3">
                            <div>
                              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Description</p>
                              <p style={{ fontSize: 13, color: '#2D2546', lineHeight: 1.55 }}>{a.description}</p>
                            </div>
                            <div className="flex gap-6">
                              <div><p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Prochaine action</p><p style={{ fontSize: 13, color: '#2D2546' }}>{a.nextAction}</p></div>
                              <div><p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Date prévue</p><p style={{ fontSize: 13, color: new Date(a.nextActionDate) < new Date() ? '#DC2626' : '#2D2546', fontWeight: 500 }}>📅 {fmtDate(a.nextActionDate)}</p></div>
                            </div>
                            <ActivityCorrectionList activity={a} />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <ActivityFormModal onClose={() => setShowCreate(false)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-activities'] }); addNotification({ type: 'success', title: 'Activité enregistrée', message: '' }); setShowCreate(false) }} />}
    </div>
  )
}

// ============================================================================
//  MODALS
// ============================================================================

// ── Créer Opportunité ─────────────────────────────────────────────────────────

function CreateOpportunityModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ title: '', stageId: '', amount: '', probability: '', expectedCloseDate: '', source: '', description: '', contactId: '', companyId: '' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const { data: stages = [] } = useQuery({ queryKey: ['crm-stages'], queryFn: () => crmApi.getStages() })
  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createOpportunity({
      title: form.title, stageId: form.stageId,
      amount: form.amount ? Number(form.amount) : undefined,
      probability: form.probability ? Number(form.probability) : undefined,
      expectedCloseDate: form.expectedCloseDate || undefined,
      source: form.source || undefined,
      description: form.description || undefined,
      contactId: form.contactId || undefined,
      companyId: form.companyId || undefined,
    }),
    onSuccess: onCreated,
  })
  return (
    <Modal title="Nouvelle opportunité" onClose={onClose}>
      <Field label="Titre *"><input className={inputCls} value={form.title} onChange={f('title')} placeholder="Nom de l'opportunité" /></Field>
      <Field label="Étape *">
        <select className={inputCls} value={form.stageId} onChange={f('stageId')}>
          <option value="">Sélectionner…</option>
          {(stages as any[]).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Montant (XOF)"><input className={inputCls} type="number" value={form.amount} onChange={f('amount')} placeholder="0" /></Field>
        <Field label="Probabilité (%)"><input className={inputCls} type="number" min="0" max="100" value={form.probability} onChange={f('probability')} placeholder="50" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact">
          <ContactCombobox ariaLabel="Contact" value={form.contactId || null} onChange={id => setForm(p => ({ ...p, contactId: id ?? '' }))} />
        </Field>
        <Field label="Entreprise">
          <CompanyCombobox ariaLabel="Entreprise" value={form.companyId || null} onChange={id => setForm(p => ({ ...p, companyId: id ?? '' }))} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Source"><select className={inputCls} value={form.source} onChange={f('source')}><option value="">— Sélectionner —</option>{LEAD_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
        <Field label="Date de clôture prévue"><input className={inputCls} type="date" value={form.expectedCloseDate} onChange={f('expectedCloseDate')} /></Field>
      </div>
      <Field label="Description"><textarea className={inputCls + ' resize-none'} rows={2} value={form.description} onChange={f('description')} placeholder="Détails de l'opportunité…" /></Field>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.title || !form.stageId}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
        </button>
      </div>
    </Modal>
  )
}

// ── Détail Opportunité ────────────────────────────────────────────────────────

function OpportunityDetailModal({ opp, onClose, onAddJournal }: { opp: CrmOpportunity; onClose: () => void; onAddJournal?: () => void }) {
  const queryClient     = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const [editMode, setEditMode] = useState(false)
  const { data } = useQuery({ queryKey: ['crm-opp', opp.id], queryFn: () => crmApi.getOpportunity(opp.id) })
  const detail = data ?? opp
  const { data: stagesData } = useQuery({ queryKey: ['crm-stages'], queryFn: () => crmApi.getStages(), enabled: editMode })
  const stages = stagesData ?? []
  const [form, setForm] = useState({ title: detail.title ?? '', amount: detail.amount?.toString() ?? '', probability: detail.probability?.toString() ?? '', expectedCloseDate: detail.expectedCloseDate ? detail.expectedCloseDate.slice(0, 10) : '', description: detail.description ?? '', stageId: detail.stage?.id ?? '' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const iCls = 'w-full px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)] bg-[#F8F7FB] border border-[var(--bd)] text-[var(--brand-dark)]'
  const updateMutation = useMutation({
    mutationFn: () => crmApi.updateOpportunity(detail.id, { title: form.title || undefined, amount: form.amount ? Number(form.amount) : undefined, probability: form.probability ? Number(form.probability) : undefined, expectedCloseDate: form.expectedCloseDate || undefined, description: form.description || undefined, stageId: form.stageId || undefined }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['crm-opp', opp.id] }); queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] }); addNotification({ type: 'success', title: 'Opportunité mise à jour', message: '' }); setEditMode(false) },
  })

  return (
    <Modal title={editMode ? "Modifier l'opportunité" : detail.title} onClose={onClose}>
      {!editMode ? (
        <>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-[var(--tx3)] text-xs mb-0.5">Étape</p><p className="text-[var(--brand-dark)] font-medium">{detail.stage?.name ?? '—'}</p></div>
            <div><p className="text-[var(--tx3)] text-xs mb-0.5">Montant</p><p className="text-[#059669] font-bold">{fmtMoney(detail.amount, detail.currency)}</p></div>
            <div><p className="text-[var(--tx3)] text-xs mb-0.5">Probabilité</p><p className="text-[var(--brand-dark)]">{detail.probability ?? 0}%</p></div>
            <div><p className="text-[var(--tx3)] text-xs mb-0.5">Score</p><span className={`text-xs px-2 py-0.5 rounded-full ${SCORE_CONFIG[detail.scoreLevel]?.bg} ${SCORE_CONFIG[detail.scoreLevel]?.color}`}>{SCORE_CONFIG[detail.scoreLevel]?.label ?? '—'}</span></div>
            {detail.expectedCloseDate && <div><p className="text-[var(--tx3)] text-xs mb-0.5">Clôture prévue</p><p className="text-[var(--brand-dark)]">{fmtDate(detail.expectedCloseDate)}</p></div>}
            {detail.contact && <div><p className="text-[var(--tx3)] text-xs mb-0.5">Contact</p><p className="text-[var(--brand-dark)]">{detail.contact.firstName} {detail.contact.lastName}</p></div>}
          </div>
          {detail.description && <div><p className="text-xs text-[var(--tx3)] mb-1">Description</p><p className="text-sm text-[var(--tx2)]">{detail.description}</p></div>}
          <div>
            <p className="text-xs text-[var(--tx3)] mb-2">Activités récentes ({(detail as any).activities?.length ?? 0})</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {((detail as any).activities ?? []).slice(0, 5).map((act: any) => {
                const Icon = ACTIVITY_ICONS[act.type] ?? Activity
                return <div key={act.id} className="flex items-start gap-2 text-xs"><div className="p-1 bg-[var(--blue)]/20 rounded shrink-0"><Icon className="w-3 h-3 text-[var(--blue-d)]" /></div><div><div className="flex items-center gap-1.5 flex-wrap"><p className="text-white font-medium">{act.subject}</p><ActivityCorrectedBadge activity={act} /></div><p className="text-[var(--tx3)]">{fmtDate(act.performedAt)}</p></div></div>
              })}
              {((detail as any).activities ?? []).length === 0 && <p className="text-[var(--tx3)] text-xs">Aucune activité</p>}
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t border-[var(--bd)]">
            <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium" style={{ background: '#F4F2FC', color: 'var(--brand-amber)' }}><Edit2 className="w-3.5 h-3.5" /> Modifier</button>
            {onAddJournal && <button onClick={() => { onClose(); onAddJournal() }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold"><BookOpen className="w-4 h-4" /> + Journal</button>}
            <button onClick={onClose} className="px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--tx3)]">Fermer</button>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-3">
            <Field label="Titre *"><input className={iCls} value={form.title} onChange={f('title')} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Montant (XOF)"><input className={iCls} type="number" value={form.amount} onChange={f('amount')} /></Field>
              <Field label="Probabilité (%)"><input className={iCls} type="number" min="0" max="100" value={form.probability} onChange={f('probability')} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Clôture prévue"><input className={iCls} type="date" value={form.expectedCloseDate} onChange={f('expectedCloseDate')} /></Field>
              <Field label="Étape"><select className={iCls} value={form.stageId} onChange={f('stageId')}><option value="">— Sélectionner —</option>{stages.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
            </div>
            <Field label="Description"><textarea className={iCls + ' resize-none'} rows={3} value={form.description} onChange={f('description')} /></Field>
          </div>
          <div className="flex gap-2 pt-2 border-t border-[var(--bd)]">
            <button onClick={() => setEditMode(false)} className="px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--tx3)]">Annuler</button>
            <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending || !form.title.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
              {updateMutation.isPending ? <Spinner /> : <Check className="w-4 h-4" />} Sauvegarder
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

// ── Journal d'activité ────────────────────────────────────────────────────────

function JournalModal({ opportunityId, onClose, onSaved }: { opportunityId: string; onClose: () => void; onSaved: () => void }) {
  const addNotif = useNotificationStore(s => s.addNotification)
  const [form, setForm] = useState({ type: 'CALL', subject: '', description: '', nextAction: '', nextActionDate: '', interestLevel: 'MEDIUM' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createActivity({ ...form, opportunityId }),
    onSuccess: () => { addNotif({ id: Date.now().toString(), type: 'success', title: 'Activité enregistrée' }); onSaved() },
  })
  return (
    <Modal title="📝 Journal d'activité (obligatoire)" onClose={onClose}>
      <div className="p-3 bg-[#FEF3C7] border border-amber-200 rounded-lg"><p className="text-xs text-amber-700">Ce compte-rendu est requis pour avancer dans le pipeline.</p></div>
      <Field label="Type *"><select className={inputCls} value={form.type} onChange={f('type')}>{Object.keys(ACTIVITY_CONFIG).map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
      <Field label="Sujet *"><input className={inputCls} value={form.subject} onChange={f('subject')} placeholder="Objet de l'interaction" /></Field>
      <Field label="Compte-rendu *"><textarea className={inputCls} rows={3} value={form.description} onChange={f('description')} placeholder="Ce qui s'est passé…" /></Field>
      <Field label="Prochaine action *"><input className={inputCls} value={form.nextAction} onChange={f('nextAction')} placeholder="Ex: Envoyer la proposition" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date prochaine action *"><input className={inputCls} type="date" value={form.nextActionDate} onChange={f('nextActionDate')} /></Field>
        <Field label="Niveau d'intérêt"><select className={inputCls} value={form.interestLevel} onChange={f('interestLevel')}>{Object.keys(INTEREST_CONFIG).map(l => <option key={l} value={l}>{l}</option>)}</select></Field>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.subject || !form.description || !form.nextAction || !form.nextActionDate}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Check className="w-4 h-4" />} Enregistrer
        </button>
      </div>
    </Modal>
  )
}

// ── Créer / Modifier Contact ──────────────────────────────────────────────────

function CreateContactModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', companyId: '', linkedinUrl: '', source: '', notes: '', tags: [] as string[] })
  const [tagInput, setTagInput] = useState('')
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const addTag = () => { const t = tagInput.trim(); if (t && !form.tags.includes(t)) setForm(p => ({ ...p, tags: [...p.tags, t] })); setTagInput('') }
  const { mutate, isPending } = useMutation({ mutationFn: () => crmApi.createContact({ ...form, email: form.email.trim() || undefined, phone: form.phone.trim() || undefined, companyId: form.companyId || undefined, source: form.source || undefined, linkedinUrl: form.linkedinUrl || undefined, mobile: form.mobile.trim() || undefined, notes: form.notes.trim() || undefined, tags: form.tags.length ? form.tags : undefined }), onSuccess: onCreated })
  return (
    <Modal title="Nouveau contact" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3"><Field label="Prénom *"><input className={inputCls} value={form.firstName} onChange={f('firstName')} autoFocus /></Field><Field label="Nom *"><input className={inputCls} value={form.lastName} onChange={f('lastName')} /></Field></div>
      <div className="grid grid-cols-2 gap-3"><Field label="Email"><input className={inputCls} type="email" value={form.email} onChange={f('email')} /></Field><Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={f('phone')} placeholder="+221 77…" /></Field></div>
      <div className="grid grid-cols-2 gap-3"><Field label="Mobile"><input className={inputCls} value={form.mobile} onChange={f('mobile')} /></Field><Field label="Poste"><input className={inputCls} value={form.jobTitle} onChange={f('jobTitle')} /></Field></div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Entreprise"><CompanyCombobox ariaLabel="Entreprise" value={form.companyId || null} onChange={id => setForm(p => ({ ...p, companyId: id ?? '' }))} /></Field>
        <Field label="Source"><select className={inputCls} value={form.source} onChange={f('source')}><option value="">— Sélectionner —</option>{LEAD_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
      </div>
      <Field label="LinkedIn"><input className={inputCls} value={form.linkedinUrl} onChange={f('linkedinUrl')} placeholder="https://linkedin.com/in/…" /></Field>
      <Field label="Tags">
        <div className="flex gap-2"><input className={inputCls + ' flex-1'} value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Tag + Entrée" /><button onClick={addTag} className="px-3 py-2 bg-[#F4F2FC] rounded-lg text-xs text-[var(--brand-amber)]">+ Ajouter</button></div>
        {form.tags.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{form.tags.map(t => <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background: 'var(--blue-bg)', color: 'var(--brand-amber)' }}>#{t}<button onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }))}>×</button></span>)}</div>}
      </Field>
      <Field label="Notes"><textarea className={inputCls + ' resize-none'} rows={2} value={form.notes} onChange={f('notes')} /></Field>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.firstName || !form.lastName}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
        </button>
      </div>
    </Modal>
  )
}

function EditContactModal({ contact, onClose, onSaved }: { contact: CrmContact; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ firstName: contact.firstName, lastName: contact.lastName, email: contact.email ?? '', phone: contact.phone ?? '', jobTitle: contact.jobTitle ?? '', companyId: contact.companyId ?? '', source: contact.source ?? '' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const { mutate, isPending } = useMutation({ mutationFn: () => crmApi.updateContact(contact.id, { ...form, companyId: form.companyId || undefined, source: form.source || undefined }), onSuccess: onSaved })
  return (
    <Modal title={`Modifier — ${contact.firstName} ${contact.lastName}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3"><Field label="Prénom *"><input className={inputCls} value={form.firstName} onChange={f('firstName')} /></Field><Field label="Nom *"><input className={inputCls} value={form.lastName} onChange={f('lastName')} /></Field></div>
      <div className="grid grid-cols-2 gap-3"><Field label="Email"><input className={inputCls} type="email" value={form.email} onChange={f('email')} /></Field><Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={f('phone')} /></Field></div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Poste"><input className={inputCls} value={form.jobTitle} onChange={f('jobTitle')} /></Field>
        <Field label="Entreprise"><CompanyCombobox ariaLabel="Entreprise" value={form.companyId || null} initialLabel={contact.company?.name} onChange={id => setForm(p => ({ ...p, companyId: id ?? '' }))} /></Field>
      </div>
      <Field label="Source"><select className={inputCls} value={form.source} onChange={f('source')}><option value="">— Sélectionner —</option>{LEAD_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.firstName || !form.lastName}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Check className="w-4 h-4" />} Mettre à jour
        </button>
      </div>
    </Modal>
  )
}

// ── Séquence sur un contact ───────────────────────────────────────────────────

function ContactSequenceModal({ contact, onClose }: { contact: CrmContact; onClose: () => void }) {
  const addNotif = useNotificationStore(s => s.addNotification)
  const queryClient = useQueryClient()
  const [sequenceId, setSequenceId] = useState('')
  const { data: seqData } = useQuery({ queryKey: ['crm-sequences-active'], queryFn: () => crmApi.listSequences({ isActive: true }) })
  const sequences = seqData?.items ?? []
  const startMutation = useMutation({
    mutationFn: () => crmApi.startContactSequence(contact.id, { sequenceId }),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-contacts'] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence démarrée sur le contact' }); onClose() },
    onError:    (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })
  const stopMutation = useMutation({
    mutationFn: () => crmApi.stopContactSequence(contact.id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-contacts'] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence arrêtée' }); onClose() },
  })
  return (
    <Modal title={`Séquence — ${contact.firstName} ${contact.lastName}`} onClose={onClose}>
      {!contact.email && <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-xs text-red-700">⚠ Ce contact n'a pas d'adresse email. Ajoutez-en une avant de démarrer une séquence.</p></div>}
      <Field label="Séquence à démarrer">
        <select className={inputCls} value={sequenceId} onChange={e => setSequenceId(e.target.value)} disabled={!contact.email}>
          <option value="">Sélectionner une séquence…</option>
          {(sequences as any[]).map((s: any) => <option key={s.id} value={s.id}>{s.name} ({s.steps?.length ?? 0} étapes)</option>)}
        </select>
      </Field>
      <div className="flex gap-2 pt-2">
        <button onClick={() => stopMutation.mutate()} disabled={stopMutation.isPending}
          className="px-4 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 disabled:opacity-50">
          {stopMutation.isPending ? <Spinner /> : 'Arrêter la séquence active'}
        </button>
        <button onClick={() => startMutation.mutate()} disabled={!sequenceId || !contact.email || startMutation.isPending}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
          {startMutation.isPending ? <Spinner /> : <Zap className="w-4 h-4" />} Démarrer
        </button>
      </div>
    </Modal>
  )
}

// ── CompanyFormModal ──────────────────────────────────────────────────────────

function CompanyFormModal({ company, onClose, onSaved }: { company?: any; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!company
  const [form, setForm] = useState({ name: company?.name ?? '', industry: company?.industry ?? '', size: company?.size ?? '', website: company?.website ?? '', phone: company?.phone ?? '', email: company?.email ?? '', address: company?.address ?? '', city: company?.city ?? '', country: company?.country ?? 'Sénégal', ninea: company?.ninea ?? '', rccm: company?.rccm ?? '', notes: company?.notes ?? '' })
  const [tags, setTags]     = useState<string[]>(company?.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const addTag = () => { const t = tagInput.trim(); if (t && !tags.includes(t)) setTags(p => [...p, t]); setTagInput('') }
  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const payload = { ...form, tags, website: form.website.trim() || undefined, phone: form.phone.trim() || undefined, email: form.email.trim() || undefined, address: form.address.trim() || undefined, city: form.city.trim() || undefined, country: form.country.trim() || undefined, ninea: form.ninea.trim() || undefined, rccm: form.rccm.trim() || undefined, notes: form.notes.trim() || undefined, industry: form.industry || undefined, size: form.size || undefined }
      return isEdit ? crmApi.updateCompany(company.id, payload) : crmApi.createCompany(payload)
    },
    onSuccess: onSaved,
  })
  return (
    <Modal title={isEdit ? `Modifier — ${company.name}` : 'Nouvelle entreprise'} onClose={onClose}>
      <Field label="Nom *"><input className={inputCls} value={form.name} onChange={f('name')} autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Secteur"><select className={inputCls} value={form.industry} onChange={f('industry')}><option value="">—</option>{INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}</select></Field>
        <Field label="Taille"><select className={inputCls} value={form.size} onChange={f('size')}><option value="">—</option>{COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
      </div>
      <div className="grid grid-cols-2 gap-3"><Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={f('phone')} /></Field><Field label="Email"><input className={inputCls} type="email" value={form.email} onChange={f('email')} /></Field></div>
      <Field label="Site web"><input className={inputCls} value={form.website} onChange={f('website')} placeholder="https://…" /></Field>
      <div className="grid grid-cols-2 gap-3"><Field label="Ville"><input className={inputCls} value={form.city} onChange={f('city')} /></Field><Field label="Pays"><input className={inputCls} value={form.country} onChange={f('country')} /></Field></div>
      <div className="grid grid-cols-2 gap-3"><Field label="NINEA"><input className={inputCls} value={form.ninea} onChange={f('ninea')} /></Field><Field label="RCCM"><input className={inputCls} value={form.rccm} onChange={f('rccm')} /></Field></div>
      <Field label="Tags">
        <div className="flex gap-2"><input className={inputCls + ' flex-1'} value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Tag + Entrée" /><button onClick={addTag} className="px-3 py-2 bg-[#F4F2FC] rounded-lg text-xs text-[var(--brand-amber)]">+ Ajouter</button></div>
        {tags.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{tags.map(t => <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background: 'var(--blue-bg)', color: 'var(--brand-amber)' }}>#{t}<button onClick={() => setTags(p => p.filter(x => x !== t))}>×</button></span>)}</div>}
      </Field>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.name.trim()}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} {isEdit ? 'Mettre à jour' : 'Créer'}
        </button>
      </div>
    </Modal>
  )
}

// ── ActivityFormModal ─────────────────────────────────────────────────────────

function ActivityFormModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const now  = new Date()
  const [form, setForm] = useState({ type: 'CALL' as string, subject: '', description: '', nextAction: '', nextActionDate: new Date(now.getTime() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 10), interestLevel: 'MEDIUM' as string, durationMinutes: '' as string | number, contactId: '', opportunityId: '', performedAt: now.toISOString().slice(0, 16) })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const { data: oppsData }     = useQuery({ queryKey: ['crm-opps-modal'],    queryFn: () => crmApi.getPipeline() })
  const opps     = oppsData?.flatMap((col: any) => col.opportunities ?? []) ?? []
  const cfg      = ACTIVITY_CONFIG[form.type] ?? ACTIVITY_CONFIG.OTHER
  const canSubmit = form.subject.trim() && form.description.trim() && form.nextAction.trim() && form.nextActionDate
  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createActivity({ type: form.type, subject: form.subject.trim(), description: form.description.trim(), nextAction: form.nextAction.trim(), nextActionDate: new Date(form.nextActionDate).toISOString(), interestLevel: form.interestLevel || undefined, durationMinutes: form.durationMinutes ? Number(form.durationMinutes) : undefined, contactId: form.contactId || undefined, opportunityId: form.opportunityId || undefined, performedAt: new Date(form.performedAt).toISOString() }),
    onSuccess: onSaved,
  })
  return (
    <Modal title="Saisir une activité" onClose={onClose}>
      <div>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Type *</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(ACTIVITY_CONFIG).map(([type, c]) => (
            <button key={type} onClick={() => setForm(p => ({ ...p, type }))} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{ background: form.type === type ? cfg.bg : '#F4F2FC', color: form.type === type ? cfg.color : 'var(--tx3)', border: `1.5px solid ${form.type === type ? cfg.color : 'transparent'}` }}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>
      <Field label="Objet *"><input className={inputCls} value={form.subject} onChange={f('subject')} autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact"><ContactCombobox ariaLabel="Contact" value={form.contactId || null} onChange={id => setForm(p => ({ ...p, contactId: id ?? '' }))} /></Field>
        <Field label="Opportunité"><select className={inputCls} value={form.opportunityId} onChange={f('opportunityId')}><option value="">— Aucune —</option>{opps.map((o: any) => <option key={o.id} value={o.id}>{o.title}</option>)}</select></Field>
      </div>
      <Field label="Description *"><textarea className={inputCls + ' resize-none'} rows={3} value={form.description} onChange={f('description')} /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Intérêt"><select className={inputCls} value={form.interestLevel} onChange={f('interestLevel')}>{Object.entries(INTEREST_CONFIG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}</select></Field>
        <Field label="Durée (min)"><input className={inputCls} type="number" min={0} value={form.durationMinutes} onChange={f('durationMinutes')} /></Field>
        <Field label="Date/heure *"><input className={inputCls} type="datetime-local" value={form.performedAt} onChange={f('performedAt')} /></Field>
      </div>
      <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 12 }}>
        <Field label="Prochaine action *"><input className={inputCls} value={form.nextAction} onChange={f('nextAction')} /></Field>
        <Field label="Date prévue *"><input className={inputCls} type="date" value={form.nextActionDate} onChange={f('nextActionDate')} /></Field>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !canSubmit}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Enregistrer
        </button>
      </div>
    </Modal>
  )
}

export { TargetsPage, SequencesPage } from './CrmTargetsSequences'
